import React, { useMemo, useState } from "react";
import theme from "../../../../../styles/theme";
import drawSpaceConfig from "../config/drawSpaceConfig";

/*
====================================================================
SOUS-COMPOSANTS UI
====================================================================

Ce fichier contient :
- des petits composants purement visuels
- puis le composant principal de sidebar hiérarchique
*/

const ChevronDown = "/assets/icones/chevron-down.svg";
const ChevronUp = "/assets/icones/chevron-up.svg";

/*
Section visuelle standardisée de la sidebar.

Chaque section comprend :
- un titre
- un bloc d’actions à droite
- un contenu libre en dessous
*/
const SidebarSection = ({ title, actions, children, style = {} }) => (
  <div
    style={{
      marginBottom: 18,
      ...style,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 8,
      }}
    >
      <div
        style={{
          fontFamily: theme.fonts.body,
          fontSize: theme.fontSizes.sm,
          fontWeight: theme.fontWeights.regular,
          color: theme.colors.text.third,
          letterSpacing: "0.2px",
        }}
      >
        {title}
      </div>

      <div
        style={{
          display: "flex",
          gap: 4,
        }}
      >
        {actions}
      </div>
    </div>

    {children}
  </div>
);

/*
Item simple utilisé dans l’onglet Create.

Il gère :
- l’état actif
- l’état "viewActive" lié au mode courant
- un point coloré indicatif
*/
const Item = ({
  active,
  viewActive = false,
  label,
  onClick,
  onDoubleClick = () => {},
  dotColor = theme.colors.text.secondary,
}) => (
  <div
    onClick={onClick}
    onDoubleClick={onDoubleClick}
    style={{
      padding: "5px 7px",
      cursor: "pointer",
      borderRadius: theme.borders.radius.sm,
      fontSize: theme.fontSizes.xs,
      background: active ? theme.Generic.blue.pastel.bg : "transparent",
      color: active ? theme.Generic.blue.pastel.text : theme.colors.text.body,
      border: viewActive
        ? `1px solid ${theme.Generic.blue.pastel}`
        : "1px solid transparent",
      boxShadow: viewActive ? theme.shadows.xs : "none",
      marginBottom: 6,
      userSelect: "none",
      transition: "all 0.15s ease-in-out",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: dotColor,
        flexShrink: 0,
      }}
    />

    <span
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  </div>
);

/*
Petit bouton d’action standardisé pour :
- création
- suppression
*/
const ActionButton = ({ children, onClick, disabled = false, title = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      height: 20,
      padding: "0 10px",
      borderRadius: 2,
      border: `1px solid ${theme.colors.borders.default}`,
      background: disabled
        ? theme.colors.background.muted
        : theme.colors.background.surface,
      color: disabled
        ? theme.colors.text.verysmall
        : theme.colors.text.body,
      fontFamily: theme.fonts.body,
      fontSize: theme.fontSizes.xs,
      fontWeight: theme.fontWeights.regular,
      letterSpacing: "0.1px",
      cursor: disabled ? "not-allowed" : "pointer",
      boxShadow: "none",
      transition: "all 0.15s ease-in-out",
      lineHeight: 0.5,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </button>
);

/*
Bouton d’onglet du haut de sidebar.
*/
const TabButton = ({ active, label, onClick }) => (
  <button
    onClick={onClick}
    style={{
      flex: 1,
      height: 22,
      margin: 0,
      padding: 2,
      borderRadius: theme.borders.radius.md,
      border: `1px solid ${theme.colors.borders.default}`,
      background: active
        ? theme.Generic.blue.selected.bg
        : theme.colors.background.surface,
      color: active
        ? theme.Generic.blue.selected.text
        : theme.colors.text.body,
      fontFamily: theme.fonts.body,
      fontSize: theme.fontSizes.xs,
      cursor: "pointer",
    }}
  >
    {label}
  </button>
);

const CreationKindToggle = ({ value = "ENTITY", onChange = () => {} }) => (
  <div
    style={{
      display: "flex",
      gap: 4,
      padding: 4,
      border: `1px solid ${theme.colors.borders.default}`,
      borderRadius: theme.borders.radius.md,
      background: theme.colors.background.surface,
    }}
  >
    <button
      onClick={() => onChange("EMPRISE")}
      style={{
        flex: 1,
        height: 24,
        borderRadius: theme.borders.radius.sm,
        border: "none",
        background:
          value === "EMPRISE" ? theme.Generic.blue.selected.bg : "transparent",
        color:
          value === "EMPRISE"
            ? theme.Generic.blue.selected.text
            : theme.colors.text.body,
        fontFamily: theme.fonts.body,
        fontSize: theme.fontSizes.xs,
        cursor: "pointer",
      }}
    >
      Emprise
    </button>

    <button
      onClick={() => onChange("ENTITY")}
      style={{
        flex: 1,
        height: 24,
        borderRadius: theme.borders.radius.sm,
        border: "none",
        background:
          value === "ENTITY" ? theme.Generic.blue.selected.bg : "transparent",
        color:
          value === "ENTITY"
            ? theme.Generic.blue.selected.text
            : theme.colors.text.body,
        fontFamily: theme.fonts.body,
        fontSize: theme.fontSizes.xs,
        cursor: "pointer",
      }}
    >
      Entity
    </button>
  </div>
);

/*
====================================================================
COMPOSANT PRINCIPAL
====================================================================

Cette sidebar a deux rôles :
1. navigation hiérarchique
2. pilotage de la visibilité structurelle

Onglets :
- Create   : listes simples + actions + choix du contenu à créer
- Views    : vue arborescente avec repli/dépli + visibilité
- Systems  : vue arborescente des systems / subsystems
*/
const DrawSiteLevelSidebar = ({
  sites = [],

  activeSiteId,
  activeOuvrageId,
  activeLevelId,

  activeEntities = [],
  selectedEntityId = null,
  onSelectEntity = () => {},

  activeSystemId = null,
  activeSubsystemId = null,

  activeMode,
  setActiveMode = () => {},

  setActiveSiteId = () => {},
  setActiveOuvrageId = () => {},
  setActiveLevelId = () => {},

  createSite = () => {},
  deleteSite = () => {},
  updateSiteAttributes = () => {},

  createOuvrage = () => {},
  deleteOuvrage = () => {},
  updateOuvrageById = () => {},

  createLevel = () => {},
  deleteLevel = () => {},
  updateLevelById = () => {},

  systems = [],

  setActiveSystemId = () => {},
  setActiveSubsystemId = () => {},

  creationKind = "ENTITY",
  setCreationKind = () => {},

  onVisibilityChange = () => {},

  style = {},
}) => {
  /*
  ===============================================================
  ÉTATS LOCAUX DE SIDEBAR
  ===============================================================
  */
  const [activeTab, setActiveTab] = useState("layout");
  const [collapsedSites, setCollapsedSites] = useState({});
  const [collapsedOuvrages, setCollapsedOuvrages] = useState({});
  const [hoveredId, setHoveredId] = useState(null);
  const [hiddenSites, setHiddenSites] = useState({});
  const [hiddenOuvrages, setHiddenOuvrages] = useState({});
  const [hiddenLevels, setHiddenLevels] = useState({});
  const [collapsedSystems, setCollapsedSystems] = useState({});
  const [hiddenSystems, setHiddenSystems] = useState({});
  const [hiddenSubsystems, setHiddenSubsystems] = useState({});
   // EDITION SITE / OUVRAGE
  const [editingSiteId, setEditingSiteId] = useState(null);
  const [editingSiteLabel, setEditingSiteLabel] = useState("");
  const clickTimeoutRef = React.useRef(null);
  const [editingOuvrageId, setEditingOuvrageId] = useState(null);
  const [editingOuvrageLabel, setEditingOuvrageLabel] = useState("");
  const [editingLevelId, setEditingLevelId] = useState(null);
  const [editingLevelLabel, setEditingLevelLabel] = useState("");

  /*
  ================================================================
  FILTRAGE HIÉRARCHIQUE
  ================================================================
  */

  const activeSite = useMemo(() => {
    return sites.find((site) => site.id === activeSiteId) ?? null;
  }, [sites, activeSiteId]);

  const visibleOuvrages = activeSite?.ouvrages ?? [];

  const activeOuvrage = useMemo(() => {
    return visibleOuvrages.find((ouvrage) => ouvrage.id === activeOuvrageId) ?? null;
  }, [visibleOuvrages, activeOuvrageId]);

  const visibleLevels = activeOuvrage?.levels ?? [];

  /*
  ================================================================
  HANDLERS DE SÉLECTION
  ================================================================
  */

  const handleSelectSite = (siteId) => {
    setActiveMode("SITE");
    setActiveSiteId(siteId);
    setActiveOuvrageId(null);
    setActiveLevelId(null);
  };

  // Ajoute edition Site label------------------------------------
    const handleStartSiteEditing = (site) => {
    if (!site) return;

    setEditingSiteId(site.id);
    setEditingSiteLabel(site.label ?? "");
  };

  const handleCancelSiteEditing = () => {
    setEditingSiteId(null);
    setEditingSiteLabel("");
  };

  const handleSubmitSiteEditing = () => {
    const nextLabel = editingSiteLabel.trim();

    if (!editingSiteId) return;

    if (!nextLabel) {
      handleCancelSiteEditing();
      return;
    }

    updateSiteAttributes(editingSiteId, {
      label: nextLabel,
    });

    handleCancelSiteEditing();
  };


  //-----------------------------------------
// Ajoute edition Site label------------------------------------
  const handleStartOuvrageEditing = (ouvrage) => {
    if (!ouvrage) return;

    setEditingOuvrageId(ouvrage.id);
    setEditingOuvrageLabel(ouvrage.label ?? "");
  };

  const handleCancelOuvrageEditing = () => {
    setEditingOuvrageId(null);
    setEditingOuvrageLabel("");
  };

  const handleSubmitOuvrageEditing = () => {
    const nextLabel = editingOuvrageLabel.trim();

    if (!editingOuvrageId) return;

    if (!nextLabel) {
      handleCancelOuvrageEditing();
      return;
    }

    updateOuvrageById(editingOuvrageId, {
      label: nextLabel,
    });

    handleCancelOuvrageEditing();
  };

//------------------------------------------------------




  const handleSelectOuvrage = (ouvrageId) => {
    setActiveMode("OUVRAGE");
    setActiveOuvrageId(ouvrageId);
    setActiveLevelId(null);
  };

  const handleSelectLevel = (levelId) => {
    setActiveLevelId(levelId);
    setActiveMode("NIVEAU");
  };

  // Ajout Corelia
const handleStartLevelEditing = (level) => {
  if (!level) return;

  setEditingLevelId(level.id);
  setEditingLevelLabel(level.label ?? "");
};

const handleCancelLevelEditing = () => {
  setEditingLevelId(null);
  setEditingLevelLabel("");
};

const handleSubmitLevelEditing = () => {
  const nextLabel = editingLevelLabel.trim();

  if (!editingLevelId) return;

  if (!nextLabel) {
    handleCancelLevelEditing();
    return;
  }

  updateLevelById(editingLevelId, {
    label: nextLabel,
  });

  handleCancelLevelEditing();
};

  //------------

  const handleSelectSystem = (systemId) => {
    const system = systems.find((item) => item.id === systemId) ?? null;
    const firstSubsystem = system?.subsystems?.[0] ?? null;

    setActiveSystemId(systemId);
    setActiveSubsystemId(firstSubsystem?.id ?? null);
  };

  const handleSelectSubsystem = (subsystemId) => {
    const parentSystem =
      systems.find((system) =>
        (system.subsystems ?? []).some((sub) => sub.id === subsystemId)
      ) ?? null;

    setActiveSystemId(parentSystem?.id ?? null);
    setActiveSubsystemId(subsystemId);
  };

  /*
  ================================================================
  HANDLERS REPLI / DÉPLI
  ================================================================
  */
  const toggleSiteCollapse = (siteId) => {
    setCollapsedSites((prev) => ({
      ...prev,
      [siteId]: !prev[siteId],
    }));
  };

  const toggleOuvrageCollapse = (ouvrageId) => {
    setCollapsedOuvrages((prev) => ({
      ...prev,
      [ouvrageId]: !prev[ouvrageId],
    }));
  };

  const toggleSystemCollapse = (systemId) => {
    setCollapsedSystems((prev) => ({
      ...prev,
      [systemId]: !prev[systemId],
    }));
  };

  /*
  ================================================================
  HANDLERS DE VISIBILITÉ
  ================================================================
  */

  const toggleSiteVisibility = (siteId) => {
    setHiddenSites((prev) => {
      const nextHidden = !prev[siteId];

      onVisibilityChange({
        type: "SITE",
        id: siteId,
        visible: !nextHidden,
      });

      return {
        ...prev,
        [siteId]: nextHidden,
      };
    });
  };

  const toggleOuvrageVisibility = (ouvrageId) => {
    setHiddenOuvrages((prev) => {
      const nextHidden = !prev[ouvrageId];

      onVisibilityChange({
        type: "OUVRAGE",
        id: ouvrageId,
        visible: !nextHidden,
      });

      return {
        ...prev,
        [ouvrageId]: nextHidden,
      };
    });
  };

  const toggleLevelVisibility = (levelId) => {
    setHiddenLevels((prev) => {
      const nextHidden = !prev[levelId];

      onVisibilityChange({
        type: "LEVEL",
        id: levelId,
        visible: !nextHidden,
      });

      return {
        ...prev,
        [levelId]: nextHidden,
      };
    });
  };

  const toggleSystemVisibility = (systemId) => {
    setHiddenSystems((prev) => {
      const nextHidden = !prev[systemId];

      onVisibilityChange({
        type: "SYSTEM",
        id: systemId,
        visible: !nextHidden,
      });

      return {
        ...prev,
        [systemId]: nextHidden,
      };
    });
  };

  const toggleSubsystemVisibility = (subsystemId) => {
    setHiddenSubsystems((prev) => {
      const nextHidden = !prev[subsystemId];

      onVisibilityChange({
        type: "SUBSYSTEM",
        id: subsystemId,
        visible: !nextHidden,
      });

      return {
        ...prev,
        [subsystemId]: nextHidden,
      };
    });
  };

  /*
  ================================================================
  COULEURS DE DOT PAR TYPE
  ================================================================
  */
  const getDotColor = (type) => {
    if (type === "SITE") {
      return (
        drawSpaceConfig?.palettes?.site?.stroke ??
        drawSpaceConfig?.palettes?.ouvrage?.stroke ??
        theme.colors.text.secondary
      );
    }

    if (type === "OUVRAGE") {
      return (
        drawSpaceConfig?.palettes?.ouvrage?.stroke ??
        drawSpaceConfig?.palettes?.level?.stroke ??
        theme.colors.text.secondary
      );
    }

    if (type === "LEVEL") {
      return (
        drawSpaceConfig?.palettes?.level?.stroke ??
        drawSpaceConfig?.palettes?.area?.stroke ??
        theme.colors.text.secondary
      );
    }

    if (type === "AREA") {
      return (
        drawSpaceConfig?.palettes?.area?.stroke ?? theme.colors.text.secondary
      );
    }

    return theme.colors.text.secondary;
  };

  /*
  ================================================================
  Helper de label d’entité
  ================================================================
  */
  const getEntityLabel = (entity) => {
    if (!entity) return "";

    if (entity.label && entity.label.trim()) return entity.label;

    if (entity.type === drawSpaceConfig.entityTypes.AREA_RECT) return "Rectangle";
    if (entity.type === drawSpaceConfig.entityTypes.AREA_POLYLINE) return "Polyline";
    if (entity.type === drawSpaceConfig.entityTypes.CIRCLE) return "Circle";
    if (entity.type === drawSpaceConfig.entityTypes.LINE) return "Line";
    if (entity.type === drawSpaceConfig.entityTypes.ARC) return "Arc";

    return "Entity";
  };

  /*
  ================================================================
  RENDER - ONGLET CREATE
  ================================================================
  */
  const renderLayoutTab = () => {
    return (
      <>
        <SidebarSection
          title="Sites"
          actions={
            <>
              <ActionButton onClick={createSite} title="Add site">
                +
              </ActionButton>

              <ActionButton
                onClick={() => deleteSite(activeSiteId)}
                disabled={!activeSiteId}
                title="Delete site"
              >
                −
              </ActionButton>
            </>
          }
          style={{
            minHeight: 90,
          }}
        >
          {sites.map((site) => (
            <div key={site.id}>
              {editingSiteId === site.id ? (
                <input
                  value={editingSiteLabel}
                  onChange={(e) => setEditingSiteLabel(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  onBlur={handleSubmitSiteEditing}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSubmitSiteEditing();
                    }
                    if (e.key === "Escape") {
                      handleCancelSiteEditing();
                    }
                  }}
                  autoFocus
                  style={{
                    width: "100%",
                    height: 24,
                    padding: "0 6px",
                    borderRadius: theme.borders.radius.sm,
                    border: `1px solid ${theme.colors.borders.default}`,
                    background: theme.colors.background.surface,
                    fontFamily: theme.fonts.body,
                    fontSize: theme.fontSizes.xs,
                    boxSizing: "border-box",
                  }}
                />
              ) : (
                <Item
                  label={site.label}
                  dotColor={getDotColor("SITE")}
                  active={site.id === activeSiteId}
                  viewActive={site.id === activeSiteId && activeMode === "SITE"}
                  onClick={() => {
                    if (clickTimeoutRef.current) {
                      clearTimeout(clickTimeoutRef.current);
                    }

                    clickTimeoutRef.current = setTimeout(() => {
                      handleSelectSite(site.id);
                    }, 180);
                  }}
                  onDoubleClick={() => {
                    if (clickTimeoutRef.current) {
                      clearTimeout(clickTimeoutRef.current);
                    }

                    handleStartSiteEditing(site);
                  }}
                />
              )}
            </div>
          ))}
        </SidebarSection>

        <SidebarSection
          title="Ouvrages"
          actions={
            <>
              <ActionButton
                onClick={createOuvrage}
                disabled={!activeSiteId}
                title="Add ouvrage"
              >
                +
              </ActionButton>

              <ActionButton
                onClick={() => deleteOuvrage(activeOuvrageId)}
                disabled={!activeOuvrageId}
                title="Delete ouvrage"
              >
                −
              </ActionButton>
            </>
          }
          style={{
            minHeight: 150,
          }}
        >
          {visibleOuvrages.map((ouvrage) => (
            <div key={ouvrage.id}>
              {editingOuvrageId === ouvrage.id ? (
                <input
                  value={editingOuvrageLabel}
                  onChange={(e) => setEditingOuvrageLabel(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  onBlur={handleSubmitOuvrageEditing}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSubmitOuvrageEditing();
                    }
                    if (e.key === "Escape") {
                      handleCancelOuvrageEditing();
                    }
                  }}
                  autoFocus
                  style={{
                    width: "100%",
                    height: 24,
                    padding: "0 6px",
                    borderRadius: theme.borders.radius.sm,
                    border: `1px solid ${theme.colors.borders.default}`,
                    background: theme.colors.background.surface,
                    fontFamily: theme.fonts.body,
                    fontSize: theme.fontSizes.xs,
                    boxSizing: "border-box",
                    marginBottom: 6,
                  }}
                />
              ) : (
                <Item
                  label={ouvrage.label}
                  dotColor={getDotColor("OUVRAGE")}
                  active={ouvrage.id === activeOuvrageId}
                  viewActive={
                    ouvrage.id === activeOuvrageId && activeMode === "OUVRAGE"
                  }
                  onClick={() => {
                    if (clickTimeoutRef.current) {
                      clearTimeout(clickTimeoutRef.current);
                    }

                    clickTimeoutRef.current = setTimeout(() => {
                      handleSelectOuvrage(ouvrage.id);
                    }, 180);
                  }}
                  onDoubleClick={() => {
                    if (clickTimeoutRef.current) {
                      clearTimeout(clickTimeoutRef.current);
                    }

                    handleStartOuvrageEditing(ouvrage);
                  }}
                />
              )}
            </div>
          ))}
        </SidebarSection>

        <SidebarSection
          title="Levels"
          actions={
            <>
              <ActionButton
                onClick={createLevel}
                disabled={!activeOuvrageId}
                title="Add level"
              >
                +
              </ActionButton>

              <ActionButton
                onClick={() => deleteLevel(activeLevelId)}
                disabled={!activeLevelId}
                title="Delete level"
              >
                −
              </ActionButton>
            </>
          }
          style={{
            minHeight: 120,
            marginBottom: 0,
          }}
        >
          {visibleLevels.map((level) => (
            <div key={level.id}>
              {editingLevelId === level.id ? (
                <input
                  value={editingLevelLabel}
                  onChange={(e) => setEditingLevelLabel(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  onBlur={handleSubmitLevelEditing}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSubmitLevelEditing();
                    }
                    if (e.key === "Escape") {
                      handleCancelLevelEditing();
                    }
                  }}
                  autoFocus
                  style={{
                    width: "100%",
                    height: 24,
                    padding: "0 6px",
                    borderRadius: theme.borders.radius.sm,
                    border: `1px solid ${theme.colors.borders.default}`,
                    background: theme.colors.background.surface,
                    fontFamily: theme.fonts.body,
                    fontSize: theme.fontSizes.xs,
                    boxSizing: "border-box",
                    marginBottom: 6,
                  }}
                />
              ) : (
                <Item
                  label={level.label}
                  dotColor={getDotColor("LEVEL")}
                  active={level.id === activeLevelId}
                  viewActive={
                    level.id === activeLevelId && activeMode === "NIVEAU"
                  }
                  onClick={() => {
                    if (clickTimeoutRef.current) {
                      clearTimeout(clickTimeoutRef.current);
                    }

                    clickTimeoutRef.current = setTimeout(() => {
                      handleSelectLevel(level.id);
                    }, 180);
                  }}
                  onDoubleClick={() => {
                    if (clickTimeoutRef.current) {
                      clearTimeout(clickTimeoutRef.current);
                    }

                    handleStartLevelEditing(level);
                  }}
                />
              )}
            </div>
          ))}
        </SidebarSection>

        <SidebarSection
          title="Entities"
          style={{
            minHeight: 120,
            marginBottom: 0,
          }}
        >
          {(activeEntities ?? []).map((entity) => (
            <Item
              key={entity.id}
              label={getEntityLabel(entity)}
              dotColor={getDotColor("AREA")}
              active={entity.id === selectedEntityId}
              viewActive={entity.id === selectedEntityId}
              onClick={() => onSelectEntity(entity.id)}
            />
          ))}
        </SidebarSection>
      </>
    );
  };

  /*
  ================================================================
  RENDER - ONGLET VIEWS
  ================================================================
  */
  const renderStructureTab = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          fontFamily: theme.fonts.body,
          fontSize: theme.fontSizes.xs,
          color: theme.colors.text.body,
          lineHeight: 1.5,
        }}
      >
        {sites.map((site) => (
          <div key={site.id}>
            {/* HEADER SITE */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <img
                src={collapsedSites[site.id] ? ChevronDown : ChevronUp}
                alt=""
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSiteCollapse(site.id);
                }}
                style={{
                  width: 10,
                  height: 10,
                  opacity: 0.7,
                  flexShrink: 0,
                  cursor: "pointer",
                }}
              />

                            <div
                onClick={() => {
                  if (clickTimeoutRef.current) {
                    clearTimeout(clickTimeoutRef.current);
                  }

                  clickTimeoutRef.current = setTimeout(() => {
                    handleSelectSite(site.id);
                  }, 180);
                }}
                onDoubleClick={() => {
                  if (clickTimeoutRef.current) {
                    clearTimeout(clickTimeoutRef.current);
                  }

                  handleStartSiteEditing(site);
                }}
                onMouseEnter={() => setHoveredId(`site-${site.id}`)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  fontWeight: theme.fontWeights.semiBold,
                  color:
                    site.id === activeSiteId
                      ? theme.colors.text.primary
                      : theme.colors.text.body,
                  cursor: "pointer",
                  padding: "2px 4px",
                  borderRadius: 4,
                  background:
                    hoveredId === `site-${site.id}`
                      ? theme.colors.background.panel
                      : "transparent",
                  transition: "background 0.12s ease",
                  flex: 1,
                }}
              >
                {editingSiteId === site.id ? (
                  <input
                    value={editingSiteLabel}
                    onChange={(e) => setEditingSiteLabel(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                    onBlur={handleSubmitSiteEditing}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSubmitSiteEditing();
                      }

                      if (e.key === "Escape") {
                        handleCancelSiteEditing();
                      }
                    }}
                    autoFocus
                    style={{
                      width: "100%",
                      height: 22,
                      padding: "0 6px",
                      borderRadius: 4,
                      border: `1px solid ${theme.colors.borders.default}`,
                      background: theme.colors.background.surface,
                      color: theme.colors.text.body,
                      fontFamily: theme.fonts.body,
                      fontSize: theme.fontSizes.xs,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                ) : (
                  <div>{site.label}</div>
                )}
              </div>

              <div
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSiteVisibility(site.id);
                }}
                style={{
                  cursor: "pointer",
                  opacity: 0.7,
                  fontSize: 12,
                }}
              >
                {hiddenSites[site.id] ? "🚫" : "👁"}
              </div>
            </div>

            {/* OUVRAGES DU SITE */}
            {!collapsedSites[site.id] && (
              <div style={{ paddingLeft: 12, marginTop: 4 }}>
                {(site.ouvrages ?? []).map((ouvrage) => (
                  <div key={ouvrage.id} style={{ marginBottom: 6 }}>
                    {/* HEADER OUVRAGE */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <img
                        src={
                          collapsedOuvrages[ouvrage.id]
                            ? ChevronDown
                            : ChevronUp
                        }
                        alt=""
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOuvrageCollapse(ouvrage.id);
                        }}
                        style={{
                          width: 10,
                          height: 10,
                          opacity: 0.7,
                          flexShrink: 0,
                          cursor: "pointer",
                        }}
                      />

                                            <div
                        onClick={() => {
                          if (clickTimeoutRef.current) {
                            clearTimeout(clickTimeoutRef.current);
                          }

                          clickTimeoutRef.current = setTimeout(() => {
                            handleSelectOuvrage(ouvrage.id);
                          }, 180);
                        }}
                        onDoubleClick={() => {
                          if (clickTimeoutRef.current) {
                            clearTimeout(clickTimeoutRef.current);
                          }

                          handleStartOuvrageEditing(ouvrage);
                        }}
                        onMouseEnter={() => setHoveredId(`ouvrage-${ouvrage.id}`)}
                        onMouseLeave={() => setHoveredId(null)}
                        style={{
                          fontWeight:
                            ouvrage.id === activeOuvrageId
                              ? theme.fontWeights.semiBold
                              : theme.fontWeights.normal,
                          color:
                            ouvrage.id === activeOuvrageId
                              ? theme.colors.text.primary
                              : theme.colors.text.body,
                          cursor: "pointer",
                          padding: "2px 4px",
                          borderRadius: 4,
                          background:
                            hoveredId === `ouvrage-${ouvrage.id}`
                              ? theme.colors.background.panel
                              : "transparent",
                          transition: "background 0.12s ease",
                          flex: 1,
                        }}
                      >
                        {editingOuvrageId === ouvrage.id ? (
                          <input
                            value={editingOuvrageLabel}
                            onChange={(e) => setEditingOuvrageLabel(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                            onBlur={handleSubmitOuvrageEditing}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleSubmitOuvrageEditing();
                              }

                              if (e.key === "Escape") {
                                handleCancelOuvrageEditing();
                              }
                            }}
                            autoFocus
                            style={{
                              width: "100%",
                              height: 22,
                              padding: "0 6px",
                              borderRadius: 4,
                              border: `1px solid ${theme.colors.borders.default}`,
                              background: theme.colors.background.surface,
                              color: theme.colors.text.body,
                              fontFamily: theme.fonts.body,
                              fontSize: theme.fontSizes.xs,
                              outline: "none",
                              boxSizing: "border-box",
                            }}
                          />
                        ) : (
                          ouvrage.label
                        )}
                      </div>

                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOuvrageVisibility(ouvrage.id);
                        }}
                        style={{
                          cursor: "pointer",
                          opacity: 0.7,
                          fontSize: 12,
                        }}
                      >
                        {hiddenOuvrages[ouvrage.id] ? "🚫" : "👁"}
                      </div>
                    </div>

                    {/* LEVELS DE L’OUVRAGE */}
                    {!collapsedOuvrages[ouvrage.id] && (
                      <div style={{ paddingLeft: 12, marginTop: 2 }}>
                        {(ouvrage.levels ?? []).map((level) => (
                        <div
                          key={level.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          {editingLevelId === level.id ? (
                            <input
                              value={editingLevelLabel}
                              onChange={(e) => setEditingLevelLabel(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onDoubleClick={(e) => e.stopPropagation()}
                              onBlur={handleSubmitLevelEditing}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleSubmitLevelEditing();
                                }
                                if (e.key === "Escape") {
                                  handleCancelLevelEditing();
                                }
                              }}
                              autoFocus
                              style={{
                                flex: 1,
                                height: 22,
                                padding: "0 6px",
                                borderRadius: theme.borders.radius.sm,
                                border: `1px solid ${theme.colors.borders.default}`,
                                background: theme.colors.background.surface,
                                fontFamily: theme.fonts.body,
                                fontSize: theme.fontSizes.xs,
                              }}
                            />
                          ) : (
                            <div
                              onClick={() => {
                                if (clickTimeoutRef.current) {
                                  clearTimeout(clickTimeoutRef.current);
                                }

                                clickTimeoutRef.current = setTimeout(() => {
                                  handleSelectLevel(level.id);
                                }, 180);
                              }}
                              onDoubleClick={() => {
                                if (clickTimeoutRef.current) {
                                  clearTimeout(clickTimeoutRef.current);
                                }

                                handleStartLevelEditing(level);
                              }}
                              onMouseEnter={() => setHoveredId(`level-${level.id}`)}
                              onMouseLeave={() => setHoveredId(null)}
                              style={{
                                flex: 1,
                                color:
                                  level.id === activeLevelId
                                    ? theme.colors.text.primary
                                    : theme.colors.text.body,
                                cursor: "pointer",
                                padding: "1px 12px",
                                borderRadius: 4,
                                background:
                                  hoveredId === `level-${level.id}`
                                    ? theme.colors.background.panel
                                    : "transparent",
                                transition: "background 0.12s ease",
                              }}
                            >
                              {level.label}
                            </div>
                          )}

                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLevelVisibility(level.id);
                            }}
                            style={{
                              cursor: "pointer",
                              opacity: 0.7,
                              fontSize: 12,
                            }}
                          >
                            {hiddenLevels[level.id] ? "🚫" : "👁"}
                          </div>
                        </div>
                      ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  /*
  ================================================================
  RENDER - ONGLET SYSTEMS
  ================================================================
  */
  const renderSystemsTab = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          fontFamily: theme.fonts.body,
          fontSize: theme.fontSizes.xs,
          color: theme.colors.text.body,
          lineHeight: 1.5,
        }}
      >
        {systems.map((system) => (
          <div key={system.id}>
            {/* HEADER SYSTEM */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <img
                src={collapsedSystems[system.id] ? ChevronDown : ChevronUp}
                alt=""
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSystemCollapse(system.id);
                }}
                style={{
                  width: 10,
                  height: 10,
                  opacity: 0.7,
                  cursor: "pointer",
                }}
              />

              <div
                onClick={() => handleSelectSystem(system.id)}
                onMouseEnter={() => setHoveredId(`system-${system.id}`)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  fontWeight:
                    system.id === activeSystemId
                      ? theme.fontWeights.semiBold
                      : theme.fontWeights.normal,
                  color:
                    system.id === activeSystemId
                      ? theme.colors.text.primary
                      : theme.colors.text.body,
                  cursor: "pointer",
                  padding: "2px 4px",
                  borderRadius: 4,
                  background:
                    hoveredId === `system-${system.id}`
                      ? theme.colors.background.panel
                      : "transparent",
                  transition: "background 0.12s ease",
                  flex: 1,
                }}
              >
                {system.label}
              </div>

              <div
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSystemVisibility(system.id);
                }}
                style={{
                  cursor: "pointer",
                  opacity: 0.7,
                  fontSize: 12,
                }}
              >
                {hiddenSystems[system.id] ? "🚫" : "👁"}
              </div>
            </div>

            {/* SUBSYSTEMS */}
            {!collapsedSystems[system.id] && (
              <div style={{ paddingLeft: 12, marginTop: 4 }}>
                {(system.subsystems ?? []).map((sub) => (
                  <div
                    key={sub.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 4,
                    }}
                  >
                    <div
                      onClick={() => handleSelectSubsystem(sub.id)}
                      onMouseEnter={() => setHoveredId(`sub-${sub.id}`)}
                      onMouseLeave={() => setHoveredId(null)}
                      style={{
                        flex: 1,
                        cursor: "pointer",
                        padding: "1px 8px",
                        borderRadius: 4,
                        color:
                          sub.id === activeSubsystemId
                            ? theme.colors.text.primary
                            : theme.colors.text.body,
                        background:
                          hoveredId === `sub-${sub.id}`
                            ? theme.colors.background.panel
                            : "transparent",
                        transition: "background 0.12s ease",
                        fontWeight:
                          sub.id === activeSubsystemId
                            ? theme.fontWeights.semiBold
                            : theme.fontWeights.normal,
                      }}
                    >
                      {sub.label}
                    </div>

                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSubsystemVisibility(sub.id);
                      }}
                      style={{
                        cursor: "pointer",
                        opacity: 0.7,
                        fontSize: 12,
                      }}
                    >
                      {hiddenSubsystems[sub.id] ? "🚫" : "👁"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  /*
  ================================================================
  RENDER FINAL
  ================================================================
  */
  return (
    <div
      style={{
        width: 180,
        minWidth: 180,
        background: "transparent",
        padding: 6,
        margin: 0,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        ...style,
      }}
    >
      {/* Onglets de mode d’affichage */}
      <div
        style={{
          display: "flex",
          gap: 3,
          marginBottom: 7,
          padding: 2,
        }}
      >
        <TabButton
          label="Create"
          active={activeTab === "layout"}
          onClick={() => setActiveTab("layout")}
        />

        <TabButton
          label="Views"
          active={activeTab === "structure"}
          onClick={() => setActiveTab("structure")}
        />

        <TabButton
          label="Systems"
          active={activeTab === "systems"}
          onClick={() => setActiveTab("systems")}
        />
      </div>

      {/* Contenu actif */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
          paddingRight: 2,
        }}
      >
        {activeTab === "layout" && (
          <>
            {/* ZONE HAUTE — STRUCTURE */}
            <div
              style={{
                paddingBottom: 8,
                borderBottom: `1px solid ${theme.colors.borders.default}`,
                overflowY: "auto",
              }}
            >
              {renderLayoutTab()}
            </div>

            {/* ZONE BASSE — CONTENU */}
            <div
              style={{
                paddingTop: 8,
                marginTop: 8,
              }}
            >
              <div
                style={{
                  fontFamily: theme.fonts.body,
                  fontSize: theme.fontSizes.xs,
                  color: theme.colors.text.small,
                  marginBottom: 6,
                }}
              >
                Content
              </div>

              <CreationKindToggle
                value={creationKind}
                onChange={setCreationKind}
              />
            </div>
          </>
        )}

        {activeTab === "structure" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflowY: "auto",
            }}
          >
            {renderStructureTab()}
          </div>
        )}

        {activeTab === "systems" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflowY: "auto",
            }}
          >
            {renderSystemsTab()}
          </div>
        )}
      </div>
    </div>
  );
};

export default DrawSiteLevelSidebar;